import { FC, ReactNode, useMemo } from 'react';
import { AccessTimeRounded, BoltRounded, CalendarMonthRounded } from '@mui/icons-material';
import { Typography } from '@mui/material';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import './TeamDayResourceHeader.scss';
import { useMinuteTickContext } from '../../../../hooks';
import { isValidTimezone } from '../../../../utils';
import type { TeamDayResource } from '../types';

interface TeamDayResourceHeaderProps {
  index: number;
  label: ReactNode;
  resource: Record<string, unknown>;
}

const TeamDayResourceHeader: FC<TeamDayResourceHeaderProps> = ({ resource }) => {
  const { t } = useTranslation();
  const member = resource as unknown as TeamDayResource;
  const isCalendarHidden = !member.isCalendarShared;
  const isEnergyHidden = !member.isEnergyShared;
  const now = useMinuteTickContext();

  const { localNow, dayOffsetLabel } = useMemo(() => {
    if (!isValidTimezone(member.timezone)) {
      return { localNow: null, dayOffsetLabel: '' };
    }

    const resourceNow = now.tz(member.timezone);
    const viewerNow = now.tz(dayjs.tz.guess());
    const dayDelta = resourceNow.startOf('day').diff(viewerNow.startOf('day'), 'day');
    const offsetLabel = dayDelta > 0 ? ` (+${dayDelta}d)` : dayDelta < 0 ? ` (${dayDelta}d)` : '';

    return { localNow: resourceNow, dayOffsetLabel: offsetLabel };
  }, [member.timezone, now]);

  return (
    <div className="team-day-resource-header">
      <div className="team-day-resource-header__avatar" aria-hidden="true">
        {member.photoUrl ? <img src={member.photoUrl} alt="" /> : member.initials}
      </div>
      <div className="team-day-resource-header__copy">
        <Typography variant="body2" component="span" className="team-day-resource-header__name">
          {member.name}
        </Typography>
        <div className="team-day-resource-header__meta">
          <div
            className="team-day-resource-header__signals"
            aria-label={t('team.day.memberSharingAria', { name: member.name })}
          >
            <span
              className={clsx(
                'team-day-resource-header__signal',
                isCalendarHidden && 'team-day-resource-header__signal--muted'
              )}
              title={
                isCalendarHidden ? t('team.day.calendarNotShared') : t('team.day.calendarShared')
              }
            >
              <CalendarMonthRounded />
            </span>
            <span
              className={clsx(
                'team-day-resource-header__signal',
                isEnergyHidden && 'team-day-resource-header__signal--muted'
              )}
              title={isEnergyHidden ? t('team.day.energyNotShared') : t('team.day.energyShared')}
            >
              <BoltRounded />
            </span>
          </div>
          {localNow && (
            <span
              className="team-day-resource-header__time"
              title={t('team.day.memberLocalTime', {
                name: member.name,
                offset: dayOffsetLabel,
              })}
            >
              <AccessTimeRounded />
              <span className="team-day-resource-header__time-value">
                {localNow.format('h:mm A')}
                {dayOffsetLabel}
              </span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamDayResourceHeader;
